import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquarePlus, Star, X, Loader2, Send } from 'lucide-react';
import { useAuth, API } from '../App';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'bug', label: 'Bug' },
  { value: 'idea', label: 'Idea' },
  { value: 'praise', label: 'Praise' },
];

// Tester feedback — always in English (IDFC official language). Visible to authenticated members only.
const FeedbackWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;

  const reset = () => {
    setRating(0);
    setHover(0);
    setCategory('general');
    setMessage('');
  };

  const submit = async () => {
    if (!rating) {
      toast.error('Please pick a rating first');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        rating,
        message,
        category,
        page: location.pathname,
      });
      toast.success('Thank you! Your feedback was sent to the team.');
      setOpen(false);
      reset();
    } catch (err) {
      toast.error('Could not send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="feedback-fab"
        title="Send feedback"
        className="fixed right-5 z-[61] flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#45B08F] text-white text-sm font-semibold shadow-lg shadow-black/30 hover:bg-[#3A9679] hover:scale-105 transition-transform"
        style={{ bottom: 60 }}
      >
        <MessageSquarePlus className="w-4 h-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="bg-[#184252] border-[#285D71] max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-[#45B08F]" />
              Share your experience
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Tell us how the platform feels. Your feedback goes straight to the team.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-1">
            {/* Rating */}
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Overall rating</p>
              <div className="flex items-center gap-1.5" data-testid="feedback-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    data-testid={`feedback-star-${n}`}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-7 h-7 ${(hover || rating) >= n ? 'text-[#F5C544] fill-[#F5C544]' : 'text-slate-500'}`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Category */}
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Type</p>
              <div className="flex flex-wrap gap-2" data-testid="feedback-categories">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCategory(c.value)}
                    data-testid={`feedback-category-${c.value}`}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                      category === c.value
                        ? 'bg-[#45B08F]/15 border-[#45B08F] text-[#45B08F]'
                        : 'bg-transparent border-[#285D71] text-slate-400 hover:text-white'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Comment (optional)</p>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What worked well? What was confusing? Anything you'd change?"
                rows={4}
                maxLength={4000}
                data-testid="feedback-message"
                className="bg-[#112C38] border-[#285D71] text-white placeholder:text-slate-500 resize-none"
              />
            </div>

            <p className="text-[11px] text-slate-500">
              Sent from: <span className="font-mono text-slate-400">{location.pathname}</span>
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => { setOpen(false); reset(); }}
              className="border-[#285D71] text-slate-400"
              data-testid="feedback-cancel-btn"
            >
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-[#45B08F] hover:bg-[#3A9679]"
              data-testid="feedback-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Send feedback
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackWidget;
